import { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOffOutlined";
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from "firebase/auth";
import { FirebaseError } from "firebase/app";
import { auth } from "../lib/firebase";
import { useT } from "../i18n";
import type { StringKey } from "../i18n/fa";

const MIN_LENGTH = 8;

/** A translatable error: a string key plus any interpolation params. */
type LocalError = { key: StringKey; params?: Record<string, string | number> };

/**
 * Firebase itself accepts 6 characters. These accounts hold a hall's bookings
 * and ledger, so the panel asks for 8 — the check is client-side and advisory,
 * not a security boundary. Returns a key, not a resolved string, so the
 * message follows the chosen locale.
 */
function localValidation(current: string, next: string, repeat: string): LocalError | null {
  if (next.length < MIN_LENGTH) return { key: "cp_min_length", params: { n: MIN_LENGTH } };
  if (next !== repeat) return { key: "cp_mismatch" };
  if (next === current) return { key: "cp_same_as_current" };
  return null;
}

/** Firebase auth codes → a key the hall owner's locale can render. */
function messageKeyFor(error: unknown): StringKey {
  const code = error instanceof FirebaseError ? error.code : "";
  switch (code) {
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "cp_err_wrong_password";
    case "auth/weak-password":
      return "cp_err_weak";
    case "auth/too-many-requests":
      return "cp_err_too_many";
    case "auth/requires-recent-login":
      return "cp_err_recent_login";
    case "auth/network-request-failed":
      return "cp_err_network";
    default:
      return "cp_err_generic";
  }
}

/**
 * Change the signed-in user's own password. Firebase requires a fresh
 * credential before a password change, so the current password is
 * re-authenticated first and only then replaced.
 */
export function ChangePasswordDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const [reveal, setReveal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const t = useT();

  function close() {
    setCurrent("");
    setNext("");
    setRepeat("");
    setReveal(false);
    setError(null);
    setDone(false);
    onClose();
  }

  async function submit() {
    const user = auth.currentUser;
    if (!user?.email) {
      setError(t("cp_err_no_email"));
      return;
    }
    const invalid = localValidation(current, next, repeat);
    if (invalid) {
      setError(t(invalid.key, invalid.params));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, current));
      await updatePassword(user, next);
      setDone(true);
    } catch (e) {
      setError(t(messageKeyFor(e)));
    } finally {
      setBusy(false);
    }
  }

  const revealToggle = (
    <InputAdornment position="end">
      <IconButton
        onClick={() => setReveal((v) => !v)}
        edge="end"
        size="small"
        aria-label={reveal ? t("cp_hide_password") : t("cp_show_password")}
        aria-pressed={reveal}
      >
        {reveal ? <VisibilityOffIcon /> : <VisibilityIcon />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <Dialog open={open} onClose={busy ? undefined : close} maxWidth="xs" fullWidth>
      <DialogTitle>{t("cp_title")}</DialogTitle>
      <DialogContent>
        {done ? (
          <Alert severity="success" sx={{ mt: 1 }}>
            {t("cp_success")}
          </Alert>
        ) : (
          <Stack spacing={2} mt={1}>
            <Typography variant="body2" color="text.secondary">
              {t("cp_intro")}
            </Typography>
            <TextField
              label={t("cp_current_label")}
              type={reveal ? "text" : "password"}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              fullWidth
              autoFocus
              InputProps={{ endAdornment: revealToggle }}
            />
            <TextField
              label={t("cp_new_label")}
              type={reveal ? "text" : "password"}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              fullWidth
              helperText={t("cp_min_helper", { n: MIN_LENGTH })}
            />
            <TextField
              label={t("cp_repeat_label")}
              type={reveal ? "text" : "password"}
              value={repeat}
              onChange={(e) => setRepeat(e.target.value)}
              autoComplete="new-password"
              fullWidth
              error={repeat.length > 0 && repeat !== next}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !busy) submit();
              }}
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {done ? (
          <Button variant="contained" onClick={close}>
            {t("common_close")}
          </Button>
        ) : (
          <>
            <Button onClick={close} disabled={busy}>
              {t("common_cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={submit}
              disabled={busy || !current || !next || !repeat}
            >
              {busy ? t("cp_submitting") : t("cp_submit")}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
